import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailToEvent = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [importing, setImporting] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleExtract = async () => {
    if (!emailContent.trim()) {
      toast.error('Please paste the email content');
      return;
    }

    setLoading(true);
    setExtractedData(null);

    try {
      const response = await axios.post(
        `${API}/events/parse-email`,
        { 
          email_content: emailContent.trim(),
          subject: emailSubject.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setExtractedData(response.data.extracted_data);
        toast.success('Event details extracted successfully!');
      } else {
        toast.error('Could not extract event details');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error parsing email');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!extractedData) return;

    // Validate required fields
    if (!extractedData.title || !extractedData.date || !extractedData.start_time || 
        !extractedData.end_time || !extractedData.location || !extractedData.address) {
      toast.error('Please fill in all required fields');
      return;
    }

    setImporting(true);
    try {
      const eventData = {
        title: extractedData.title,
        description: extractedData.description || '',
        category: extractedData.category || 'other',
        region: 'nyc', // Default region
        date: extractedData.date,
        start_time: extractedData.start_time,
        end_time: extractedData.end_time,
        location: extractedData.location,
        address: extractedData.address,
        registration_link: extractedData.registration_link || ''
      };

      const response = await axios.post(`${API}/events`, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Event created successfully!');
      navigate(`/events/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating event');
    } finally {
      setImporting(false);
    }
  };

  const updateExtractedField = (field, value) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-10">
          <p className="overline text-gold mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> AI-Powered
          </p>
          <h1 className="text-3xl md:text-4xl flex items-center gap-3">
            <Mail className="w-8 h-8 text-gold" />
            Email to Event
          </h1>
          <p className="text-muted-foreground mt-2">
            Paste an event invitation email and we'll extract the details automatically
          </p>
        </div>

        {/* Email Input Section */}
        <div className="border border-border/50 p-6 bg-card mb-8">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider">Email Subject (optional)</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="e.g., You're Invited: Senior Care Networking Event"
                className="rounded-sm border-border/50"
                data-testid="email-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm uppercase tracking-wider">Email Content *</Label>
              <Textarea
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                placeholder="Paste the full email content here...

Example:
Join us for the Senior Care Professional Networking Mixer!

Date: Thursday, February 15, 2026
Time: 6:00 PM - 8:00 PM
Location: The Grand Ballroom
Address: 123 Main Street, New York, NY 10001

Network with fellow senior care professionals..."
                rows={12}
                className="rounded-sm border-border/50 font-mono text-sm"
                data-testid="email-content-input"
              />
            </div>

            <Button
              onClick={handleExtract}
              disabled={loading || !emailContent.trim()}
              className="w-full bg-gold text-primary hover:bg-gold/90"
              data-testid="extract-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Extracting Event Details...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Extract Event Details
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Extracted Data Preview/Edit */}
        {extractedData && (
          <div className="border border-gold/50 p-6 bg-gold/5 rounded-lg">
            <div className="flex items-center gap-2 mb-6">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold">Extracted Event Details</h3>
              <span className="text-xs text-muted-foreground ml-auto">
                Review and edit before creating
              </span>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Title *</Label>
                <Input
                  value={extractedData.title || ''}
                  onChange={(e) => updateExtractedField('title', e.target.value)}
                  className="rounded-sm border-border/50"
                  data-testid="extracted-title"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Description</Label>
                <Textarea
                  value={extractedData.description || ''}
                  onChange={(e) => updateExtractedField('description', e.target.value)}
                  rows={4}
                  className="rounded-sm border-border/50"
                  data-testid="extracted-description"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Date *</Label>
                  <Input
                    type="date"
                    value={extractedData.date || ''}
                    onChange={(e) => updateExtractedField('date', e.target.value)}
                    className="rounded-sm border-border/50"
                    data-testid="extracted-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Start Time *</Label>
                  <Input
                    type="time"
                    value={extractedData.start_time || ''}
                    onChange={(e) => updateExtractedField('start_time', e.target.value)}
                    className="rounded-sm border-border/50"
                    data-testid="extracted-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">End Time *</Label>
                  <Input
                    type="time"
                    value={extractedData.end_time || ''}
                    onChange={(e) => updateExtractedField('end_time', e.target.value)}
                    className="rounded-sm border-border/50"
                    data-testid="extracted-end-time"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Location *</Label>
                  <Input
                    value={extractedData.location || ''}
                    onChange={(e) => updateExtractedField('location', e.target.value)}
                    className="rounded-sm border-border/50"
                    data-testid="extracted-location"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm uppercase tracking-wider">Category</Label>
                  <Select 
                    value={extractedData.category || 'other'} 
                    onValueChange={(v) => updateExtractedField('category', v)}
                  >
                    <SelectTrigger className="rounded-sm border-border/50" data-testid="extracted-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conference">Conference</SelectItem>
                      <SelectItem value="workshop">Workshop</SelectItem>
                      <SelectItem value="networking">Networking Mixer</SelectItem>
                      <SelectItem value="webinar">Webinar</SelectItem>
                      <SelectItem value="seminar">Seminar</SelectItem>
                      <SelectItem value="training">Training Session</SelectItem>
                      <SelectItem value="panel">Panel Discussion</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Address *</Label>
                <Input
                  value={extractedData.address || ''}
                  onChange={(e) => updateExtractedField('address', e.target.value)}
                  className="rounded-sm border-border/50"
                  data-testid="extracted-address"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Registration Link (optional)</Label>
                <Input
                  value={extractedData.registration_link || ''}
                  onChange={(e) => updateExtractedField('registration_link', e.target.value)}
                  placeholder="https://..."
                  className="rounded-sm border-border/50"
                  data-testid="extracted-link"
                />
              </div>

              <div className="flex gap-4 pt-4 border-t border-border/50">
                <Button
                  variant="outline"
                  onClick={() => setExtractedData(null)}
                  className="flex-1"
                >
                  Clear
                </Button>
                <Button
                  onClick={handleCreateEvent}
                  disabled={importing}
                  className="flex-1 bg-gold text-primary hover:bg-gold/90"
                  data-testid="create-event-btn"
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Create Event
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="mt-8 p-4 bg-secondary/50 rounded-lg">
          <h4 className="font-medium mb-2">Tips for best results:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Include the full email content with all event details</li>
            <li>• Make sure dates and times are clearly stated in the email</li>
            <li>• Include the venue name and full address</li>
            <li>• You can edit any extracted details before creating the event</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmailToEvent;
